import { Gift, FileText, Shield, Clock, CreditCard, Truck, AlertCircle, CheckCircle, User, Mail, Phone } from 'lucide-react'

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      {/* Hero */}
      <div className="relative bg-gradient-to-r from-purple-600 to-pink-500 text-white py-14 md:py-20">
        <div className="absolute inset-0 bg-black/10" />
        <div className="container-spa max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full mb-4 shadow-lg">
            <FileText className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3 drop-shadow-sm">
            Terms of Service
          </h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto">
            Please read these terms before using our services.
          </p>
          <p className="text-sm text-white/80 mt-3">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="container-spa max-w-4xl mx-auto px-4 py-10 pb-16">
        {/* Gift Card Terms */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Gift className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-gray-800">Gift Card Terms & Policies</h2>
          </div>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">Types of Gift Cards</h3>
          <p className="text-gray-700 mb-6">
            Spa~Radise Luxe offers two types of gift cards:
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-purple-50/80 rounded-xl p-6 border border-purple-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Session Gift Card (selected treatment)</h4>
              <p className="text-gray-700 text-sm mb-3">
                For gifting a specific treatment. Available on each service page.
              </p>
              <p className="text-gray-600 text-sm font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm ml-2">
                <li>Choose “Send as Gift” or “Buy Session Gift Card” on the service page</li>
                <li>Enter recipient name and email/phone; add a message if you like</li>
                <li>Pay the service price; a digital code is generated and sent to the recipient</li>
                <li>Recipient redeems the code to book that specific session</li>
              </ul>
            </div>

            <div className="bg-pink-50/80 rounded-xl p-6 border border-pink-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Wellness Gift Card (any experience)</h4>
              <p className="text-gray-700 text-sm mb-3">
                Lets the recipient choose any spa experience. Available on the Gift Cards page.
              </p>
              <p className="text-gray-600 text-sm font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm ml-2">
                <li>Select amount (e.g. GHS 200, 300, or custom)</li>
                <li>Enter recipient details and optional message; complete payment</li>
                <li>Recipient gets a code and can use the value toward any service until balance or expiry</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-800 mb-4">Terms and Policies</h3>
          <div className="space-y-4">
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">1. Validity</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>All gift cards are valid for 90 days (3 months) from purchase.</li>
                    <li>Expired cards cannot be reactivated or refunded.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">2. Refunds</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>Gift cards are non-refundable and non-transferable once purchased.</li>
                    <li>Refunds may be issued only for technical errors or duplicate transactions verified by Spa~Radise Luxe.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">3. Redemption</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>Session cards: redeemable only for the specific session purchased.</li>
                    <li>Wellness cards: usable for any session or product up to the card balance; you can top up if the service costs more.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">4. Delivery</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>Digital: sent by email, SMS, or WhatsApp after payment.</li>
                    <li>Physical: optional, dispatched within 24–48 hours; delivery charges apply and depend on location.</li>
                    <li>We are not responsible for delays by third-party couriers.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">5. Lost or stolen cards</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>Spa~Radise Luxe will not replace lost, stolen, or unauthorized gift cards.</li>
                    <li>Treat your gift card code like cash; keep it secure.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">6. Business rights</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>We may suspend cards suspected of fraud.</li>
                    <li>We may update terms or pricing with notice on the website.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">7. Partial usage (Wellness only)</h4>
                  <ul className="text-gray-700 text-sm space-y-0.5">
                    <li>Unused balance stays available until the card expires (90 days).</li>
                    <li>No cash withdrawals; balance can only be used for Spa~Radise Luxe services.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Booking and Payment */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-gray-800">Booking Deposits & Payment</h2>
          </div>

          <div className="space-y-4">
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <h4 className="font-semibold text-gray-800 mb-1">1. Deposit policy</h4>
              <ul className="text-gray-700 text-sm space-y-0.5">
                <li>• Bookings may require a deposit (percentage or fixed amount of the total).</li>
                <li>• Deposits are non-refundable if you cancel, but you may reschedule (subject to availability).</li>
                <li>• The deposit is applied to your total; the balance is due on completion or as arranged.</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <h4 className="font-semibold text-gray-800 mb-1">2. Multiple people</h4>
              <ul className="text-gray-700 text-sm space-y-0.5">
                <li>• You can book for 1 to 3 people. Total price = base price × number of people.</li>
                <li>• Duration increases by 30 minutes per extra person. Deposit is based on total price.</li>
                <li>• All persons must be present at the scheduled time.</li>
              </ul>
            </div>
            <div className="border-l-4 border-purple-400 pl-4 py-2">
              <h4 className="font-semibold text-gray-800 mb-1">3. Payment options</h4>
              <ul className="text-gray-700 text-sm space-y-0.5">
                <li>• <strong>Deposit:</strong> Pay via PayStack at booking.</li>
                <li>• <strong>Gift card:</strong> Use a valid Spa~Radise Luxe gift card for all or part of the cost.</li>
                <li>• <strong>Balance:</strong> Pay the remainder after service or as arranged. All payments are processed securely (PayStack).</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SMS Notifications */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-gray-800">SMS Notifications & Reminders</h2>
          </div>
          <p className="text-gray-700 mb-4 text-sm">
            When you book, we send SMS to the number you provide:
          </p>
          <ul className="space-y-2 text-gray-700 text-sm">
            {[
              'Booking confirmation with date, time, service, and location.',
              'Reminder before your appointment (e.g. a few hours before).',
              'For gift card bookings: payment details and remaining balance where applicable.',
              'Contact details for inquiries or changes.',
            ].map((text, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* General Terms */}
        <section className="bg-white rounded-2xl shadow-lg border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">General Terms of Service</h2>
          <div className="space-y-4 text-gray-700 text-sm leading-relaxed">
            <p>
              By using Spa~Radise Luxe services, you agree to these terms.
            </p>
            <p>
              <strong className="text-gray-800">Booking:</strong> All bookings are subject to availability. We may reschedule or cancel in unforeseen circumstances and will offer a full refund or alternative. Bookings are for 1 to 3 people per appointment.
            </p>
            <p>
              <strong className="text-gray-800">Cancellation:</strong> Cancel at least 24 hours in advance to avoid fees. Late cancellations or no-shows may incur charges. Deposits are non-refundable on cancellation but you may reschedule.
            </p>
            <p>
              <strong className="text-gray-800">Payment:</strong> Deposit (PayStack) or gift card. Deposit is based on total service price (base × number of people). Balance due on completion or as arranged.
            </p>
            <p>
              <strong className="text-gray-800">Privacy:</strong> We use your information as set out in our Privacy Policy and do not share it with third parties without consent. We use your contact details for confirmations and reminders.
            </p>
            <p>
              <strong className="text-gray-800">Service changes:</strong> Spa~Radise Luxe may modify, suspend, or discontinue any service at any time.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 md:p-10 mb-8 border border-purple-200">
          <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Contact</h2>
          <p className="text-gray-700 mb-4 text-sm">
            For questions about these terms:
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6 text-gray-700 text-sm">
            <a href="mailto:sparadiseluxe@gmail.com" className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-800 font-medium">
              <Mail className="w-4 h-4 flex-shrink-0" />
              sparadiseluxe@gmail.com
            </a>
            <a href="https://wa.me/233537813579" className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-800 font-medium" target="_blank" rel="noopener noreferrer">
              <Phone className="w-4 h-4 flex-shrink-0" />
              WhatsApp: 053 781 3579
            </a>
            <a href="tel:+233202468529" className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-800 font-medium">
              <Phone className="w-4 h-4 flex-shrink-0" />
              Call: 020 246 8529
            </a>
          </div>
        </section>

        <p className="text-center text-gray-600 text-sm">
          These terms may change. By purchasing or using a Spa~Radise Luxe gift card or our services, you acknowledge that you have read, understood, and agree to these terms.
        </p>
      </div>
    </div>
  )
}

export default Terms
